import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { UserTable, TableColumnDef } from 'src/app/services/models/user-table.model';
import { CalcEngineService } from 'src/app/services/calc-engine.service';
import { CalcEngineRequest, Metric } from 'src/app/services/models/calc-engine.model';
import { Store } from '@ngrx/store';
import { selectSelectedApplication } from 'src/app/store/app-state.selectors';
import { take } from 'rxjs';
import { DatasetService } from 'src/app/services/dataset.service';
import { Dataset } from 'src/app/services/models/datasets.models';
import { Table } from 'primeng/table';

@Component({
  selector: 'bhub-table-view',
  templateUrl: './table-view.component.html',
  styleUrls: ['./table-view.component.scss'],
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatProgressSpinnerModule, TableModule, InputTextModule]
})
export class TableViewComponent implements OnInit {
  @Input() table: UserTable | null = null;
  @Output() backClick = new EventEmitter<void>();
  @ViewChild('dt') dt: Table | undefined;

  tableData: any[] = [];
  tableHeaders: string[] = [];
  loading = false;
  error: string | null = null;
  private datasetsMap: Map<number, Dataset> = new Map();

  constructor(
    private calcEngineService: CalcEngineService,
    private store: Store,
    private datasetService: DatasetService
  ) {}

  ngOnInit() {
    if (this.table) {
      // Load datasets first, then load table data
      this.datasetService.getDatasetReferences().subscribe(datasets => {
        this.datasetsMap = datasets;
        this.constructTableHeaders();
        this.loadTableData();
      });
    }
  }

  private constructTableHeaders() {
    if (!this.table) return;

    // Sort columns by ordinal to ensure correct order
    const sortedColumns = [...this.table.columnList].sort((a, b) => a.ordinal - b.ordinal);

    // Extract column names for headers
    this.tableHeaders = sortedColumns.map(column => column.columnName);
  }

  private loadTableData() {
    if (!this.table) return;

    this.loading = true;
    this.error = null;

    // Make calc-engine service call using table structure
    this.makeCalcEngineCall();
  }

  private makeCalcEngineCall() {
    if (!this.table) return;

    // Get current application ID from store
    this.store.select(selectSelectedApplication).pipe(take(1)).subscribe(applicationId => {
      if (applicationId) {
        const calcEngineRequest = this.buildCalcEngineRequest(applicationId);

        // Make the actual call and use the response
        this.calcEngineService.runCalcEngine(calcEngineRequest, false).subscribe({
          next: (response) => {
            this.processCalcEngineResponse(response);
            this.loading = false;
          },
          error: (error) => {
            console.error('Calc Engine Error:', error);
            this.error = 'Failed to load table data';
            this.loading = false;
          }
        });
      }
    });
  }

  private buildCalcEngineRequest(applicationId: number): CalcEngineRequest {
    const request = new CalcEngineRequest();
    request.applicationId = applicationId;

    // Build metricList from base connection type columns (metrics)
    const metricColumns = this.table!.columnList.filter(col => col.connectionType === 'base');
    request.metricList = metricColumns.map(col => {
      const metric = new Metric();
      metric.metricName = col.columnName;
      // Extract metric ID from columnId (e.g., "metric_1" -> 1)
      metric.metricId = parseInt(col.columnId.split('_')[1]);
      return metric;
    });

    // Build groupBy array from primary connection type columns (primary keys)
    const primaryColumns = this.table!.columnList.filter(col => col.connectionType === 'primary');
    request.groupBy = [];

    primaryColumns.forEach(col => {
      if (col.relatedFields && col.relatedFields.length > 0) {
        // Use the referenceFieldId from the first related field for groupBy (this should be the primary key)
        const referenceFieldId = col.relatedFields[0].referenceFieldId;
        if (referenceFieldId && referenceFieldId > 0) {
          request.groupBy.push(referenceFieldId.toString());
        }
      }
    });

    // Build additionalFields from secondary connection types
    request.additionalFields = {};
    const secondaryColumns = this.table!.columnList.filter(col => col.connectionType === 'secondary');

    // Track which datasets have primary keys already in groupBy
    const datasetsWithPrimaryKeys = new Set<string>();
    primaryColumns.forEach(col => {
      if (col.relatedFields && col.relatedFields.length > 0) {
        col.relatedFields.forEach(field => {
          datasetsWithPrimaryKeys.add(field.datasetId.toString());
        });
      }
    });

    secondaryColumns.forEach(col => {
      if (col.relatedFields && col.relatedFields.length > 0) {
        col.relatedFields.forEach(field => {
          const datasetId = field.datasetId.toString();
          const fieldId = field.fieldId.toString();

          if (!request.additionalFields[datasetId]) {
            request.additionalFields[datasetId] = [];
          }

          // Add field to additionalFields if not already present
          if (!request.additionalFields[datasetId].includes(fieldId)) {
            request.additionalFields[datasetId].push(fieldId);
          }

          // Auto-add primary key for this dataset if not already in groupBy
          if (!datasetsWithPrimaryKeys.has(datasetId)) {
            // Find the primary key field for this dataset
            const primaryKeyFieldId = this.getPrimaryKeyForDataset(parseInt(datasetId));
            if (primaryKeyFieldId) {
              const primaryKeyFieldIdStr = primaryKeyFieldId.toString();
              // Check if this primary key field ID is already in groupBy array
              if (!request.groupBy.includes(primaryKeyFieldIdStr)) {
                request.groupBy.push(primaryKeyFieldIdStr);
              }
              datasetsWithPrimaryKeys.add(datasetId);
            }
          }
        });
      }
    });

    // Initialize empty filters for now
    request.filters = {};
    return request;
  }

  private processCalcEngineResponse(response: any) {
    if (!this.table || !response.rowData) {
      this.tableData = [];
      return;
    }

    // Process the response data into table format
    this.tableData = response.rowData.map((rowData: any) => {
      const processedRow: any = {};

      // Sort columns by ordinal to process in correct order
      const sortedColumns = [...this.table!.columnList].sort((a, b) => a.ordinal - b.ordinal);

      sortedColumns.forEach(column => {
        if (column.columnType === 'metric') {
          // For metrics, use the metric name as key
          processedRow[column.columnName] = rowData[column.columnName] || 0;
        } else if (column.columnType === 'field') {
          // For fields, determine which field ID to use for display
          let displayValue = this.getDisplayValueForField(column, rowData);
          processedRow[column.columnName] = displayValue || 'Unknown';
        }
      });

      return processedRow;
    });

  }

  private getDisplayValueForField(column: TableColumnDef, rowData: any): any {
    if (!column.relatedFields || column.relatedFields.length === 0) {
      return 'N/A';
    }

    // For primary connection type, use the primary key value from rowData
    if (column.connectionType === 'primary') {
      const referenceFieldId = column.relatedFields[0].referenceFieldId;
      if (referenceFieldId) {
        return rowData[referenceFieldId.toString()];
      }
    }

    // For secondary connection type, use the additional field value from rowData
    if (column.connectionType === 'secondary') {
      const fieldId = column.relatedFields[0].fieldId;
      if (fieldId) {
        return rowData[fieldId.toString()];
      }
    }

    return 'Unknown';
  }

  onBack() {
    this.backClick.emit();
  }

  onRefresh() {
    this.loadTableData();
  }

  onExport() {
    // TODO: Implement export functionality
    console.log('Export table data');
  }

  onGlobalFilter(event: Event) {
    const target = event.target as HTMLInputElement;
    if (this.dt) {
      this.dt.filterGlobal(target.value, 'contains');
    }
  }

  onColumnFilter(event: Event, field: string) {
    const target = event.target as HTMLInputElement;
    if (this.dt) {
      this.dt.filter(target.value, field, 'contains');
    }
  }

  private getPrimaryKeyForDataset(datasetId: number): number | null {
    const dataset = this.datasetsMap.get(datasetId);
    if (!dataset) {
      console.warn(`Dataset ${datasetId} not found in datasetsMap`);
      return null;
    }

    // Find the primary key field in the dataset schema
    const primaryKeyField = dataset.dataTableSchemas.find(schema => schema.isPrimaryKey);
    if (!primaryKeyField) {
      console.warn(`No primary key field found for dataset ${datasetId}`);
      return null;
    }

    return primaryKeyField.fieldId;
  }
}
